import React, { useState, useEffect, useCallback } from 'react';
import { Modal, message } from 'antd';
import { FC115 } from '../../../services/scApi';
import { scApi } from '../../../services/scApi';
import { fmt } from '../helpers';

interface ColorDetailModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (colors: Partial<FC115>[]) => void;
  colors: Partial<FC115>[];
  scNo: string;
  itemSeq: number;
  salesQty: number;
}

const ColorDetailModal: React.FC<ColorDetailModalProps> = ({
  open, onClose, onSave, colors, scNo, itemSeq, salesQty,
}) => {
  const [rows, setRows] = useState<Partial<FC115>[]>([]);
  const [colorNames, setColorNames] = useState<Record<string, string>>({});

  // Reset rows when modal opens
  useEffect(() => {
    if (open) {
      const init = colors.length > 0
        ? colors.map(c => ({ ...c }))
        : [];
      setRows(init);
      // Resolve color names for existing rows
      const namesMap: Record<string, string> = {};
      const lookups = init
        .filter(r => r.fc115_11)
        .map(r =>
          scApi.lookupName('color', r.fc115_11!).then(name => {
            namesMap[r.fc115_11!] = name;
          })
        );
      Promise.all(lookups).then(() => setColorNames(namesMap));
    }
  }, [open, colors]);

  const addRow = useCallback(() => {
    const maxSeq = rows.reduce((max, r) => Math.max(max, r.fc115_03 || 0), 0);
    setRows(prev => [
      ...prev,
      {
        fc115_01: scNo,
        fc115_02: itemSeq,
        fc115_03: maxSeq + 1,
        fc115_11: '',
        fc115_14: 0,
      },
    ]);
  }, [rows, scNo, itemSeq]);

  const deleteRow = useCallback((idx: number) => {
    setRows(prev => {
      const next = prev.filter((_, i) => i !== idx);
      // Reindex fc115_03
      return next.map((r, i) => ({ ...r, fc115_03: i + 1 }));
    });
  }, []);

  const updateField = useCallback((idx: number, key: keyof FC115, value: FC115[keyof FC115]) => {
    setRows(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], [key]: value };
      return next;
    });
  }, []);

  const handleColorBlur = useCallback((idx: number, code: string) => {
    if (!code.trim()) return;
    scApi.lookupName('color', code.trim()).then(name => {
      setColorNames(prev => ({ ...prev, [code.trim()]: name }));
    });
  }, []);

  // Compute total qty
  const totalQty = rows.reduce((sum, r) => sum + (r.fc115_14 || 0), 0);

  const handleOk = () => {
    // Validate BR-V36: qty must be > 0
    for (let i = 0; i < rows.length; i++) {
      const r = rows[i];
      if (!r.fc115_14 || r.fc115_14 <= 0) {
        message.error(`第 ${i + 1} 行：顏色數量必須大於 0`);
        return;
      }
    }

    // Validate BR-V37: no duplicate color codes
    const codes = rows.map(r => (r.fc115_11 || '').trim()).filter(Boolean);
    const dupes = codes.filter((c, i) => codes.indexOf(c) !== i);
    if (dupes.length > 0) {
      const unique = Array.from(new Set(dupes));
      message.error(`色號重複: ${unique.join(', ')}`);
      return;
    }

    // Validate BR-V35: color code cannot be empty + must exist in F114
    for (let i = 0; i < rows.length; i++) {
      const code = (rows[i].fc115_11 || '').trim();
      if (!code) {
        message.error(`第 ${i + 1} 行：色號不可空白`);
        return;
      }
      // Check if color code exists (lookup result cached in colorNames)
      if (!colorNames[code]) {
        message.error(`第 ${i + 1} 行：顏色明細-查無此色號代號!`);
        return;
      }
    }

    // Validate BR-V34: total qty must equal salesQty (tolerance 0.01)
    if (rows.length > 0 && Math.abs(totalQty - salesQty) > 0.01) {
      message.error(`顏色數量合計 (${fmt(totalQty)}) 必須等於銷售數量 (${fmt(salesQty)})`);
      return;
    }

    onSave(rows);
  };

  return (
    <Modal
      title={`顏色明細 - 項次 ${itemSeq}`}
      open={open}
      onOk={handleOk}
      onCancel={onClose}
      width={600}
      okText="確認"
      cancelText="取消"
      destroyOnClose
    >
      <div style={{ marginBottom: 8 }}>
        <button
          className="btn btn-success btn-sm"
          onClick={addRow}
          style={{ marginRight: 8 }}
        >
          + 新增色號
        </button>
      </div>

      <table className="data-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr>
            <th style={{ width: 40, padding: '6px 8px', textAlign: 'center', background: '#fafafa', borderBottom: '1px solid #f0f0f0' }}>序</th>
            <th style={{ width: 100, padding: '6px 8px', background: '#fafafa', borderBottom: '1px solid #f0f0f0' }}>色號代號</th>
            <th style={{ padding: '6px 8px', background: '#fafafa', borderBottom: '1px solid #f0f0f0' }}>色號名稱</th>
            <th style={{ width: 100, padding: '6px 8px', textAlign: 'right', background: '#fafafa', borderBottom: '1px solid #f0f0f0' }}>數量</th>
            <th style={{ width: 50, padding: '6px 8px', textAlign: 'center', background: '#fafafa', borderBottom: '1px solid #f0f0f0' }}>操作</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr>
              <td colSpan={5} style={{ textAlign: 'center', padding: '16px 8px', color: '#999' }}>
                尚無顏色明細，請點擊「+ 新增色號」
              </td>
            </tr>
          )}
          {rows.map((r, i) => (
            <tr key={i}>
              <td style={{ padding: '4px 8px', textAlign: 'center', borderBottom: '1px solid #f0f0f0' }}>
                {r.fc115_03 || i + 1}
              </td>
              <td style={{ padding: '4px 8px', borderBottom: '1px solid #f0f0f0' }}>
                <input
                  type="text"
                  style={{ width: '100%', height: 28, fontSize: 13, border: '1px solid #d9d9d9', borderRadius: 4, padding: '0 8px' }}
                  value={r.fc115_11 || ''}
                  onChange={e => updateField(i, 'fc115_11', e.target.value)}
                  onBlur={e => handleColorBlur(i, e.target.value)}
                />
              </td>
              <td style={{ padding: '4px 8px', borderBottom: '1px solid #f0f0f0', fontSize: 12 }}>
                {(() => {
                  const code = (r.fc115_11 || '').trim();
                  const name = colorNames[code];
                  if (!code) return <span style={{ color: '#666' }}>--</span>;
                  if (name) return <span style={{ color: '#666' }}>{name}</span>;
                  // code entered but lookup returned empty → not found
                  if (code in colorNames) return <span style={{ color: '#ff4d4f' }}>*查無此代號*</span>;
                  return <span style={{ color: '#666' }}>--</span>;
                })()}
              </td>
              <td style={{ padding: '4px 8px', borderBottom: '1px solid #f0f0f0' }}>
                <input
                  type="number"
                  style={{ width: '100%', height: 28, fontSize: 13, textAlign: 'right', border: '1px solid #d9d9d9', borderRadius: 4, padding: '0 8px' }}
                  value={r.fc115_14 || ''}
                  onChange={e => updateField(i, 'fc115_14', parseFloat(e.target.value) || 0)}
                />
              </td>
              <td style={{ padding: '4px 8px', textAlign: 'center', borderBottom: '1px solid #f0f0f0' }}>
                <button
                  className="btn btn-danger btn-sm"
                  style={{ height: 24, padding: '0 8px', fontSize: 12 }}
                  onClick={() => deleteRow(i)}
                >
                  刪除
                </button>
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={3} style={{ padding: '8px', textAlign: 'right', fontWeight: 600, fontSize: 13, borderTop: '2px solid #f0f0f0' }}>
              數量合計: {fmt(totalQty)} / 銷售數量: {fmt(salesQty)}
            </td>
            <td style={{ padding: '8px', textAlign: 'right', fontWeight: 600, fontSize: 13, borderTop: '2px solid #f0f0f0', color: Math.abs(totalQty - salesQty) > 0.01 ? '#ff4d4f' : '#52c41a' }}>
              {Math.abs(totalQty - salesQty) > 0.01 ? '不符' : '相符'}
            </td>
            <td style={{ borderTop: '2px solid #f0f0f0' }}></td>
          </tr>
        </tfoot>
      </table>
    </Modal>
  );
};

export default React.memo(ColorDetailModal);
